import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { UserX, UserCheck, Wallet, Package, FileText } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import LoadingSpinner from '../components/LoadingSpinner';
import { getDriverDetail, editDriver, deactivateDriver } from '../api/driversApi';
import { useToast } from '../context/ToastContext';

function DriverDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchDriver() {
      try {
        const result = await getDriverDetail(id);
        if (result.success) {
          setDriver(result.driver);
          reset({
            driver_name: result.driver.driver_name,
            email: result.driver.email,
            phone: result.driver.phone,
            date_of_birth: result.driver.date_of_birth,
            default_rate: result.driver.default_rate,
            password: '',
          });
        } else {
          showToast('error', result.message || 'Failed to load driver.');
        }
      } catch (err) {
        showToast('error', 'Could not connect to server.');
      } finally {
        setLoading(false);
      }
    }

    fetchDriver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = {
        id_driver: id,
        driver_name: data.driver_name,
        email: data.email,
        phone: data.phone,
        date_of_birth: data.date_of_birth || null,
        default_rate: data.default_rate ? Number(data.default_rate) : null,
      };
      if (data.password) {
        payload.password = data.password;
      }

      const result = await editDriver(payload);
      if (result.success) {
        showToast('success', 'Driver updated successfully.');
        navigate('/drivers');
      } else {
        showToast('error', result.message || 'Failed to update driver.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    const newStatus = driver.is_active ? 0 : 1;
    try {
      const result = await deactivateDriver(id, newStatus);
      if (result.success) {
        setDriver((prev) => ({ ...prev, is_active: newStatus }));
        showToast('success', newStatus ? 'Driver reactivated.' : 'Driver deactivated.');
      } else {
        showToast('error', result.message || 'Failed to update driver status.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex' }}>
        <Sidebar />
        <div style={{ flex: 1 }}>
          <TopBar title="Loading..." />
          <LoadingSpinner fullScreen />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <TopBar
          title={driver.driver_name}
          rightAction={
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => navigate(`/loads?driver_id=${id}`)} style={styles.loadsBtn}>
                <Package size={15} style={{ marginRight: 6 }} /> View Loads
              </button>
              <button onClick={() => navigate(`/payments?driver_id=${id}`)} style={styles.paymentsBtn}>
                <Wallet size={15} style={{ marginRight: 6 }} /> View Payments
              </button>
              <button onClick={() => navigate(`/drivers/${id}/paystub`)} style={styles.paystubBtn}>
                <FileText size={15} style={{ marginRight: 6 }} /> Paystub
              </button>
              <button
                onClick={handleToggleActive}
                style={{
                  ...styles.toggleBtn,
                  backgroundColor: driver.is_active ? '#fee2e2' : '#d1fae5',
                  color: driver.is_active ? '#991b1b' : '#065f46',
                }}
              >
                {driver.is_active ? (
                  <>
                    <UserX size={15} style={{ marginRight: 6 }} /> Deactivate
                  </>
                ) : (
                  <>
                    <UserCheck size={15} style={{ marginRight: 6 }} /> Reactivate
                  </>
                )}
              </button>
            </div>
          }
        />

        <div style={{ padding: 24, maxWidth: 480 }}>
          {!driver.is_active && (
            <div style={styles.inactiveBanner}>
              This driver is currently deactivated and cannot log in.
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} style={styles.card}>
            <label style={styles.label}>Full Name</label>
            <input {...register('driver_name', { required: true })} style={styles.input} />
            {errors.driver_name && <p style={styles.errorText}>Required</p>}

            <label style={styles.label}>Email</label>
            <input {...register('email', { required: true })} type="email" style={styles.input} />
            {errors.email && <p style={styles.errorText}>Required</p>}

            <label style={styles.label}>Phone</label>
            <input {...register('phone')} style={styles.input} />

            <label style={styles.label}>Date of Birth</label>
            <input {...register('date_of_birth')} type="date" style={styles.input} />

            <label style={styles.label}>Default Pay Rate (%)</label>
            <input {...register('default_rate')} type="number" step="0.01" style={styles.input} />

            <label style={styles.label}>New Password (leave blank to keep current)</label>
            <input {...register('password')} type="password" style={styles.input} />

            <div style={styles.buttonRow}>
              <button type="button" onClick={() => navigate('/drivers')} style={styles.cancelBtn}>
                Cancel
              </button>
              <button type="submit" disabled={saving} style={styles.submitBtn}>
                {saving ? '...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const styles = {
  toggleBtn: {
    display: 'flex',
    alignItems: 'center',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  paymentsBtn: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#eff6ff',
    color: '#2563eb',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  loadsBtn: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#eff6ff',
    color: '#2563eb',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  paystubBtn: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#eff6ff',
    color: '#2563eb',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  inactiveBanner: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 16,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
    marginTop: 14,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  errorText: {
    color: '#dc2626',
    fontSize: 12,
    marginTop: 4,
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 24,
  },
  cancelBtn: {
    padding: '10px 20px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    backgroundColor: 'white',
    color: '#374151',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 20px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  },
};

export default DriverDetail;